import { DestinationOrderPanel } from './DestinationOrderPanel';
import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Building2,
  Check,
  Link2,
  FileText,
  Share2,
  Eye,
  EyeOff,
  Edit3,
  ChevronDown,
  ChevronRight,
  Lock,
  Mail,
  Settings,
  Sparkles,
  ExternalLink,
  Copy,
  Star,
  Calculator,
  CalendarClock,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { LoadingState } from '@/components/ui/LoadingState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { StepHeader } from '../StepHeader';
import { useNotificationStore } from '@/stores/notificationStore';
import { 
  ResearchService, 
  type SubmittedProposalsResponse, 
  type ShortlistWithVenue,
} from '@/services/ResearchService';
import { 
  proposalService, 
  type ProposalShareLink,
  type DestinationContent,
  type DestinationActivity,
  type DestinationEvent,
  type ResearchDestinationActivity,
  type ResearchDestinationEvent
} from '@/services/ProposalService';
import { EventsService } from '@/services/EventsService';
import { ProposalAnalyticsDrawer } from './ProposalAnalyticsDrawer';
import { DestinationEditorModal } from './DestinationEditorModal';
import { StarRating } from '@/components/ui/StarRating';
import { ShareLinkConfigDialog } from './ShareLinkConfigDialog';
import { VenueCostOverrideModal } from './VenueCostOverrideModal';
import { BudgetManager } from './BudgetManager';
import { AgendaManager } from './AgendaManager';
import { useQuery } from '@tanstack/react-query';
import { NoteVisibilityBadge } from '../../NoteIcon';

interface ProposalStepProps {
  eventId: string;
  onBack: () => void;
}

interface ContentStatus {
  hasDescription: boolean;
  hasKeyPros: boolean;
  hasConsiderations: boolean;
  hasMedia: boolean;
  hasActivities: boolean;
  hasEvents: boolean;
  allRequired: boolean;
}

function checkContentStatus(
  content: DestinationContent | null | undefined,
  activities: DestinationActivity[],
  events: DestinationEvent[]
): ContentStatus {
  if (!content) {
    return {
      hasDescription: false,
      hasKeyPros: false,
      hasConsiderations: false,
      hasMedia: false,
      hasActivities: activities.length > 0,
      hasEvents: events.length > 0,
      allRequired: false,
    };
  }

  const hasDescription = !!(content.short_description || content.long_description);
  const hasKeyPros = content.key_pros && content.key_pros.length > 0;
  const hasConsiderations = content.considerations && content.considerations.length > 0;
  const hasMedia = (content.images && content.images.length > 0) || !!content.video;

  const hasActivities = activities.length > 0;
  const hasEvents = events.length > 0;

  // allRequired: Description, KeyPros, and Media
  const allRequired = hasDescription && hasKeyPros && hasMedia;

  return {
    hasDescription,
    hasKeyPros,
    hasConsiderations,
    hasMedia,
    hasActivities,
    hasEvents,
    allRequired,
  };
}

export function ProposalStep({ eventId, onBack }: ProposalStepProps) {
  // Data state
  const [data, setData] = useState<SubmittedProposalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedDestinations, setExpandedDestinations] = useState<Set<string>>(new Set());
  const [shareLinks, setShareLinks] = useState<ProposalShareLink[]>([]);
  const [destinationContent, setDestinationContent] = useState<Map<string, DestinationContent>>(new Map());
  const [destinationActivities, setDestinationActivities] = useState<Map<string, DestinationActivity[]>>(new Map());
  const [destinationEvents, setDestinationEvents] = useState<Map<string, DestinationEvent[]>>(new Map());
  
  // Dialog state
  const [editingDestination, setEditingDestination] = useState<{ city: string; country: string; researchDestinationId?: string; weatherDestinationId?: string } | null>(null);
  // Map city+country to researchDestinationId for quick lookup
  const [destinationIdMap, setDestinationIdMap] = useState<Map<string, string>>(new Map());
  const [configLinkType, setConfigLinkType] = useState<'research' | 'proposal' | null>(null);
  const [showProposalAnalytics, setShowProposalAnalytics] = useState(false);
  const [editingVenueCost, setEditingVenueCost] = useState<ShortlistWithVenue | null>(null);
  const [updatingRecommendedId, setUpdatingRecommendedId] = useState<string | null>(null);
  const [updatingHiddenId, setUpdatingHiddenId] = useState<string | null>(null);
  const [showBudgetManager, setShowBudgetManager] = useState(false);
  const [showAgendaManager, setShowAgendaManager] = useState(false);
  
  const researchService = new ResearchService();
  const eventsService = new EventsService();
  const notificationStore = useNotificationStore();
  
  // Fetch event data for dates
  const { data: eventData } = useQuery({
    queryKey: ['research-proposal-event', eventId],
    queryFn: () => eventsService.get(eventId),
    enabled: !!eventId,
    staleTime: 30000,
  });
  
  useEffect(() => {
    loadData();
  }, [eventId]);
  
  async function loadData(quiet = false) {
    try {
      if (!quiet) setLoading(true);
      await researchService.initializeProposalActivitySources(eventId);
      // Get only approved venues
      const result = await researchService.getSubmittedProposals(eventId, 'approved');
      setData(result);
      
      // Expand all destinations by default
      const allCityKeys = result.grouped_by_city.map(g => `${g.city}-${g.country}`);
      setExpandedDestinations(new Set(allCityKeys));
      
      // Load share links
      const links = await proposalService.listShareLinks(eventId);
      setShareLinks(links);
      
      // Load destination content, activities, and events for all destinations
      // Build mapping of city+country to researchDestinationId
      const idMap = new Map<string, string>();
      const contentMap = new Map<string, DestinationContent>();
      const activitiesMap = new Map<string, DestinationActivity[]>();
      const eventsMap = new Map<string, DestinationEvent[]>();
      
      // Process each destination group
      for (const group of result.grouped_by_city) {
        const cityKey = `${group.city}-${group.country}`;
        
        // Use the saved activity owner, independently of the hotel ordering.
        const firstDestination = group.activity_sources?.find(d=>d.id===group.content_destination_id);
        if (firstDestination) {
          idMap.set(cityKey, firstDestination.id);
        }
        
        // Load content
        try {
          const content = await proposalService.getDestinationContent(group.city, group.country);
          if (content) {
            contentMap.set(cityKey, content);
          }
        } catch {
          // Content doesn't exist yet - create an empty one
          try {
            const newContent = await proposalService.upsertDestinationContent({
              city: group.city,
              country: group.country,
            });
            contentMap.set(cityKey, newContent);
          } catch (createError) {
            console.error(`Failed to create destination content for ${group.city}, ${group.country}:`, createError);
            // Continue without content if creation fails
          }
        }
        
        // Load proposal-specific activities if we have a researchDestinationId
        const researchDestinationId = firstDestination?.id;
        if (researchDestinationId) {
          try {
            let activities = await proposalService.listResearchActivities(researchDestinationId, false);
            
            // Copying from the library is an explicit user action. An empty
            // selected list is never permission to silently repopulate it.

            // Convert ResearchDestinationActivity to DestinationActivity format
            const convertedActivities: DestinationActivity[] = activities
              .filter(a => a.is_included)
              .map(item => ({
                id: item.id,
                created_by_organization_id: null,
                city: group.city,
                country: group.country,
                name: item.name,
                description: item.description,
                category: item.category,
                intensity: item.intensity,
                duration_hours: item.duration_hours,
                estimated_price_per_person: item.estimated_price_per_person,
                currency_code: item.currency_code,
                images: item.images,
                sort_order: item.sort_order,
                is_active: item.is_included,
                last_updated_by_user_id: null,
                created_at: item.created_at,
                updated_at: item.updated_at,
              }));
            activitiesMap.set(cityKey, convertedActivities);
          } catch {
            activitiesMap.set(cityKey, []);
          }
          
          // The event modal edits this exact city-based list.
          try {
            const events = await proposalService.listEvents(group.city, group.country, false);
            eventsMap.set(cityKey, events.filter(e=>e.is_active));
          } catch {
            eventsMap.set(cityKey, []);
          }
        } else {
          // No chosen owner means no activities, not the global library.
          activitiesMap.set(cityKey, []);
          try {
            const events = await proposalService.listEvents(group.city, group.country, false);
            eventsMap.set(cityKey, events.filter(e => e.is_active));
          } catch {
            eventsMap.set(cityKey, []);
          }
        }
      }
      
      setDestinationIdMap(idMap);
      
      setDestinationContent(contentMap);
      setDestinationActivities(activitiesMap);
      setDestinationEvents(eventsMap);
    } catch (error) {
      console.error('Failed to load approved venues:', error);
      notificationStore.error('Failed to load approved venues', { title: 'Load Error' });
    } finally {
      setLoading(false);
    }
  }
  
  function toggleDestination(cityKey: string) {
    setExpandedDestinations(prev => {
      const next = new Set(prev);
      if (next.has(cityKey)) {
        next.delete(cityKey);
      } else {
        next.add(cityKey);
      }
      return next;
    });
  }
  
  // Get active links by type
  const researchLink = shareLinks.find(l => l.link_type === 'research' && l.is_active);
  const proposalLink = shareLinks.find(l => l.link_type === 'proposal' && l.is_active);

  // Lightweight analytics summary for the live badge (has the client opened it?)
  const [analyticsSummary, setAnalyticsSummary] = useState<{ view_count: number; first_viewed_at: string | null } | null>(null);
  useEffect(() => {
    let cancelled = false;
    proposalService
      .getProposalAnalytics(eventId)
      .then(a => {
        if (cancelled) return;
        const link0 = a?.links?.[0];
        setAnalyticsSummary(link0 ? { view_count: link0.view_count ?? 0, first_viewed_at: link0.first_viewed_at } : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [eventId, shareLinks]);

  function getFullUrl(link: ProposalShareLink | undefined) {
    if (!link) return null;
    const prefix = link.link_type === 'proposal' ? 'p' : 'r';
    return `${window.location.origin}/${prefix}/${link.token}`;
  }
  
  function copyLink(link: ProposalShareLink) {
    const url = getFullUrl(link);
    if (url) {
      navigator.clipboard.writeText(url);
      notificationStore.success('Link copied to clipboard', { title: 'Copied' });
    }
  }
  
  // Handle share link updates
  function handleLinkCreated(link: ProposalShareLink) {
    setShareLinks(prev => [...prev, link]);
  }
  
  function handleLinkUpdated(link: ProposalShareLink) {
    setShareLinks(prev => prev.map(l => l.id === link.id ? link : l));
  }
  
  function handleLinkDeleted(linkId: string) {
    setShareLinks(prev => prev.filter(l => l.id !== linkId));
  }
  
  // Handle destination content updates
  async function handleDestinationContentSaved(content: DestinationContent) {
    const cityKey = `${content.city}-${content.country}`;
    
    // Update content
    setDestinationContent(prev => {
      const next = new Map(prev);
      next.set(cityKey, content);
      return next;
    });
    
    // Reload activities and events
    try {
      const activities = await proposalService.listActivities(content.city, content.country, false);
      setDestinationActivities(prev => {
        const next = new Map(prev);
        next.set(cityKey, activities.filter(a => a.is_active));
        return next;
      });
      
      const events = await proposalService.listEvents(content.city, content.country, false);
      setDestinationEvents(prev => {
        const next = new Map(prev);
        next.set(cityKey, events.filter(e => e.is_active));
        return next;
      });
    } catch (error) {
      console.error('Failed to reload activities/events:', error);
    }
  }

  // Handle venue cost override updates
  function handleVenueCostSaved() {
    // Reload data to reflect changes
    loadData();
  }

  // Handle recommended toggle
  async function handleToggleRecommended(shortlistId: string, isRecommended: boolean) {
    try {
      setUpdatingRecommendedId(shortlistId);
      
      // Optimistically update local state
      if (data) {
        const updatedData = { ...data };
        let updated = false;
        
        // Update in grouped_by_city structure
        for (const cityGroup of updatedData.grouped_by_city) {
          for (const destGroup of cityGroup.destinations) {
            const item = destGroup.shortlist.find(s => s.id === shortlistId);
            if (item) {
              item.is_recommended = isRecommended;
              updated = true;
              break;
            }
          }
          if (updated) break;
        }
        
        if (updated) {
          setData(updatedData);
        }
      }
      
      // Update on server
      await researchService.updateShortlistItem(shortlistId, {
        is_recommended: isRecommended,
      });
      
      notificationStore.success(isRecommended ? 'Venue marked as recommended' : 'Recommendation removed');
    } catch (error) {
      console.error('Failed to update recommendation:', error);
      notificationStore.error('Failed to update recommendation', { title: 'Update Error' });
      // Reload data on error to sync with server
      await loadData();
    } finally {
      setUpdatingRecommendedId(null);
    }
  }

  // Handle hide-from-proposal toggle
  // Hidden venues stay in the database (and visible here in the admin) but are
  // excluded from the client-facing proposal (Destination + Comparison tabs).
  async function handleToggleHidden(shortlistId: string, isHidden: boolean) {
    try {
      setUpdatingHiddenId(shortlistId);

      // Optimistically update local state
      if (data) {
        const updatedData = { ...data };
        let updated = false;

        for (const cityGroup of updatedData.grouped_by_city) {
          for (const destGroup of cityGroup.destinations) {
            const item = destGroup.shortlist.find(s => s.id === shortlistId);
            if (item) {
              item.is_hidden = isHidden;
              updated = true;
              break;
            }
          }
          if (updated) break;
        }

        if (updated) {
          setData(updatedData);
        }
      }

      await researchService.updateShortlistItem(shortlistId, {
        is_hidden: isHidden,
      });

      notificationStore.success(
        isHidden ? 'Venue hidden from client proposal' : 'Venue visible on client proposal'
      );
    } catch (error) {
      console.error('Failed to update hidden state:', error);
      notificationStore.error('Failed to update hidden state', { title: 'Update Error' });
      await loadData();
    } finally {
      setUpdatingHiddenId(null);
    }
  }

  // Calculate stats
  const approvedCount = data?.totals?.approved ?? 0;
  const destinationCount = data?.grouped_by_city?.length ?? 0;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingState text="Loading approved venues..." />
      </div>
    );
  }
  
  if (!data || approvedCount === 0) {
    return (
      <div className="flex-1 flex flex-col">
        <StepHeader
          title="Generate & Send Proposal"
          subtitle="Create beautiful proposals for your client"
          onBack={onBack}
          backLabel="Back to Selection"
        />
        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="max-w-md" padding="lg">
            <CardContent className="text-center space-y-4">
              <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto">
                <Building2 className="w-8 h-8 text-warning-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">No Approved Venues</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Go back to the Selection step and approve at least one venue to generate a proposal.
                </p>
              </div>
              <Button variant="secondary" onClick={onBack}>
                Back to Selection
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <StepHeader
        title="Generate & Send Proposal"
        subtitle={`${approvedCount} approved venue${approvedCount !== 1 ? 's' : ''} across ${destinationCount} destination${destinationCount !== 1 ? 's' : ''}`}
        onBack={onBack}
        backLabel="Back to Selection"
      />
      
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4 sm:p-6" style={{ backgroundColor: '#efede7' }}>
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Share Links Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary-600" />
                Shareable Links
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Research Link */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4 text-secondary-600" />
                        Research Link
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Share your venue research with the client
                      </p>
                    </div>
                    {researchLink ? (
                      <Badge variant="success" className="text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        <Lock className="w-3 h-3 mr-1" />
                        Not Set
                      </Badge>
                    )}
                  </div>
                  
                  {researchLink && (
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => copyLink(researchLink)}
                        leftIcon={<Copy className="w-4 h-4" />}
                      >
                        Copy
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => window.open(getFullUrl(researchLink)!, '_blank')}
                        leftIcon={<ExternalLink className="w-4 h-4" />}
                      >
                        Open
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      leftIcon={<Settings className="w-4 h-4" />}
                      onClick={() => setConfigLinkType('research')}
                    >
                      Configure
                    </Button>
                    {!researchLink && (
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        leftIcon={<Link2 className="w-4 h-4" />}
                        onClick={() => setConfigLinkType('research')}
                      >
                        Generate Link
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shows: Shortlisted (approved), Viable (researched/RFP), All researched venues
                  </p>
                </div>
                
                {/* Proposal Link */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-accent-600" />
                        Proposal Link
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Send the final proposal to your client
                      </p>
                    </div>
                    {proposalLink ? (
                      analyticsSummary?.first_viewed_at ? (
                        <Badge variant="success" className="text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          Opened{analyticsSummary.view_count > 1 ? ` ${analyticsSummary.view_count}×` : ''}
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="text-xs">
                          <EyeOff className="w-3 h-3 mr-1" />
                          Not opened yet
                        </Badge>
                      )
                    ) : (
                      <Badge variant="primary" className="text-xs">
                        <Lock className="w-3 h-3 mr-1" />
                        Not Set
                      </Badge>
                    )}
                  </div>

                  {proposalLink && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyLink(proposalLink)}
                        leftIcon={<Copy className="w-4 h-4" />}
                      >
                        Copy
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(getFullUrl(proposalLink)!, '_blank')}
                        leftIcon={<ExternalLink className="w-4 h-4" />}
                      >
                        Open
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowProposalAnalytics(true)}
                        leftIcon={<Eye className="w-4 h-4" />}
                      >
                        View analytics
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      leftIcon={<Settings className="w-4 h-4" />}
                      onClick={() => setConfigLinkType('proposal')}
                    >
                      Configure
                    </Button>
                    {!proposalLink && (
                      <Button 
                        variant="accent" 
                        size="sm" 
                        leftIcon={<Link2 className="w-4 h-4" />}
                        onClick={() => setConfigLinkType('proposal')}
                      >
                        Generate Link
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shows: Destination overview, Hotels, Activities, Budget
                  </p>
                </div>
              </div>
              
              {/* Email Domain Whitelist Info */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 text-gray-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-gray-700">Email Verification Required</p>
                    <p className="text-muted-foreground">
                      Recipients will need to verify their email address to access these links.
                      Configure allowed email domains in the link settings.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Budget Manager Section */}
          {approvedCount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-amber-600" />
                  Budget Manager
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    Configure detailed budgets for each venue including room breakdowns, contingency, planning overhead, and profit margins. Currency conversion supported.
                  </p>
                  <Button
                    variant="secondary"
                    className="sm:flex-shrink-0"
                    onClick={() => setShowBudgetManager(true)}
                    leftIcon={<Calculator className="w-4 h-4" />}
                  >
                    Open Budget Manager
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Prelim Agenda Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-teal-600" />
                Prelim Agenda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Build a high-level, drag-and-drop sample agenda for the event week. Toggle it on to show it
                  on the client proposal (linked above Comparison).
                </p>
                <Button
                  variant="secondary"
                  className="sm:flex-shrink-0"
                  onClick={() => setShowAgendaManager(true)}
                  leftIcon={<CalendarClock className="w-4 h-4" />}
                >
                  Open Agenda Maker
                </Button>
              </div>
            </CardContent>
          </Card>

          <DestinationOrderPanel key={eventId} eventId={eventId} groups={data.grouped_by_city}
            onSaved={(groups) => setData(current => current ? { ...current, grouped_by_city: groups } : current)} />

          {/* Approved Venues by Destination */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success-600" />
                Approved Venues ({approvedCount})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.grouped_by_city.map((group) => {
                const cityKey = `${group.city}-${group.country}`;
                const isExpanded = expandedDestinations.has(cityKey);
                const content = destinationContent.get(cityKey);
                const activities = destinationActivities.get(cityKey) || [];
                const events = destinationEvents.get(cityKey) || [];
                const status = checkContentStatus(content, activities, events);

                // Check if this group is a region (from group-level fields or first destination)
                const isRegion = (group as any).destination_type === 'region'
                  || group.destinations[0]?.destination?.destination_type === 'region';
                const groupLabel = (group as any).display_name
                  || group.destinations[0]?.destination?.display_name
                  || group.city;

                // Count all venues across all destinations in this city
                const venueCount = group.destinations.reduce(
                  (sum, dest) => sum + dest.shortlist.length,
                  0
                );
                
                return (
                  <div key={cityKey} className="border rounded-lg overflow-hidden">
                    {/* Destination Header */}
                    <button
                      onClick={() => toggleDestination(cityKey)}
                      className="w-full px-4 py-3 flex items-center justify-between gap-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0" />
                        <div className="text-left min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{groupLabel}</h3>
                          <p className="text-sm text-muted-foreground truncate">{group.country}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {content && (
                          <Badge 
                            variant={status.allRequired ? "success" : "warning"} 
                            className="text-xs hidden sm:inline-flex"
                          >
                            {status.allRequired ? 'Complete' : 'Incomplete'}
                          </Badge>
                        )}
                        <Badge variant="success" className="text-xs">
                          {venueCount}
                        </Badge>
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>
                    
                    {/* Venues List */}
                    {isExpanded && (
                      <div className="p-4 space-y-4 bg-white">
                        {/* Destination Content Editor */}
                        <div className={`border-2 border-dashed rounded-lg p-4 ${
                          !content 
                            ? 'border-gray-200 bg-gray-50' 
                            : status.allRequired 
                              ? 'border-success-200 bg-success-50' 
                              : 'border-warning-200 bg-warning-50'
                        }`}>
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Edit3 className={`w-4 h-4 flex-shrink-0 ${
                                    !content 
                                      ? 'text-gray-400' 
                                      : status.allRequired 
                                        ? 'text-success-600' 
                                        : 'text-warning-600'
                                  }`} />
                                  <span className={`text-sm font-medium ${
                                    !content 
                                      ? 'text-gray-500' 
                                      : status.allRequired 
                                        ? 'text-success-700' 
                                        : 'text-warning-700'
                                  }`}>
                                    Destination Content
                                  </span>
                                </div>
                                {content ? (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                    {content.short_description || content.long_description || 'Content configured'}
                                  </p>
                                ) : (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Add description, images, and key highlights
                                  </p>
                                )}
                              </div>
                              <Button
                                variant={content ? 'secondary' : 'primary'}
                                size="sm"
                                className="sm:flex-shrink-0"
                                onClick={() => {
                                  const researchDestinationId = destinationIdMap.get(`${group.city}-${group.country}`);
                                  setEditingDestination({ 
                                    city: group.city, 
                                    country: group.country,
                                    researchDestinationId,
                                    weatherDestinationId: group.destinations[0]?.destination.id,
                                  });
                                }}
                                leftIcon={<Edit3 className="w-4 h-4" />}
                              >
                                {content ? 'Edit' : 'Add'} Content
                              </Button>
                            </div>
                            
                            {/* Content Checklist - Compact Horizontal Layout */}
                            {content && (
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                  <span className="text-gray-500 font-medium mr-1">Required:</span>
                                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    status.hasDescription ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                                  }`}>
                                    {status.hasDescription ? (
                                      <Check className="w-3 h-3" />
                                    ) : (
                                      <X className="w-3 h-3" />
                                    )}
                                    <span>Description</span>
                                  </div>
                                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    status.hasKeyPros ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                                  }`}>
                                    {status.hasKeyPros ? (
                                      <Check className="w-3 h-3" />
                                    ) : (
                                      <X className="w-3 h-3" />
                                    )}
                                    <span>Key Pros</span>
                                  </div>
                                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    status.hasMedia ? 'bg-success-100 text-success-700' : 'bg-warning-100 text-warning-700'
                                  }`}>
                                    {status.hasMedia ? (
                                      <Check className="w-3 h-3" />
                                    ) : (
                                      <X className="w-3 h-3" />
                                    )}
                                    <span>{content.video ? 'Video' : 'Images'}</span>
                                  </div>

                                  <span className="text-gray-400 mx-1">•</span>
                                  <span className="text-gray-500 font-medium mr-1">Optional:</span>
                                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    status.hasConsiderations ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {status.hasConsiderations ? (
                                      <Check className="w-3 h-3" />
                                    ) : (
                                      <X className="w-3 h-3" />
                                    )}
                                    <span>Considerations</span>
                                  </div>
                                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    status.hasActivities ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {status.hasActivities ? (
                                      <Check className="w-3 h-3" />
                                    ) : (
                                      <X className="w-3 h-3" />
                                    )}
                                    <span>Activities ({activities.length})</span>
                                  </div>
                                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                                    status.hasEvents ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {status.hasEvents ? (
                                      <Check className="w-3 h-3" />
                                    ) : (
                                      <X className="w-3 h-3" />
                                    )}
                                    <span>Events ({events.length})</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Venue Cards - iterate through destinations in this city */}
                        {group.destinations.flatMap((dest) =>
                          dest.shortlist.map((item: ShortlistWithVenue) => (
                            <div
                              key={item.id}
                              className={`border rounded-lg p-4 bg-success-50 border-success-200 ${item.is_hidden ? 'opacity-60' : ''}`}
                            >
                              <div className="flex flex-col gap-3">
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className="font-medium text-gray-900">
                                    {item.venue.property_name}
                                  </h4>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button
                                      variant={item.is_recommended ? "primary" : "ghost"}
                                      size="sm"
                                      onClick={() => handleToggleRecommended(item.id, !item.is_recommended)}
                                      disabled={updatingRecommendedId === item.id}
                                      title={item.is_recommended ? 'Remove recommendation' : 'Mark as recommended'}
                                    >
                                      <Star className={item.is_recommended ? "w-4 h-4 fill-current" : "w-4 h-4"} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditingVenueCost(item)}
                                      title="Edit venue costs"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant={item.is_hidden ? "primary" : "ghost"}
                                      size="sm"
                                      onClick={() => handleToggleHidden(item.id, !item.is_hidden)}
                                      disabled={updatingHiddenId === item.id}
                                      title={
                                        item.is_hidden
                                          ? 'Hidden from client proposal — click to show'
                                          : 'Hide from client proposal (stays saved, not visible on Destination or Comparison tabs)'
                                      }
                                    >
                                      {item.is_hidden ? (
                                        <EyeOff className="w-4 h-4" />
                                      ) : (
                                        <Eye className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  {item.is_recommended && (
                                    <Badge variant="primary" className="text-xs">
                                      <Star className="w-3 h-3 mr-1 fill-current" />
                                      Recommended
                                    </Badge>
                                  )}
                                  {item.is_hidden && (
                                    <Badge variant="warning" className="text-xs">
                                      <EyeOff className="w-3 h-3 mr-1" />
                                      Hidden from proposal
                                    </Badge>
                                  )}
                                  <Badge variant="success" className="text-xs">
                                    <Check className="w-3 h-3 mr-1" />
                                    Approved
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {item.venue.city}{item.venue.country ? `, ${item.venue.country}` : ''}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                  {item.venue.number_of_rooms && (
                                    <span>{item.venue.number_of_rooms} rooms</span>
                                  )}
                                  {item.venue.maximum_capacity && (
                                    <span>Max {item.venue.maximum_capacity} capacity</span>
                                  )}
                                  {item.venue.star_rating && (
                                    <StarRating rating={item.venue.star_rating} size="sm" showEmptyStars={true} />
                                  )}
                                </div>
                                {/* Proposal Costs - Check override first, then fall back to original */}
                                {(() => {
                                  const displayTotalCost = item.override_total_cost ?? item.cvent_total_cost;
                                  const displayLowestRate = item.override_lowest_room_rate ?? item.cvent_lowest_room_rate;

                                  if (displayTotalCost || displayLowestRate) {
                                    return (
                                      <div className="flex items-center gap-4 text-sm">
                                        {displayTotalCost ? (
                                          <span className="text-success-700 font-medium">
                                            Total: ${displayTotalCost.toLocaleString()}
                                            {item.override_total_cost && (
                                              <span className="ml-1 text-xs text-gray-500">(override)</span>
                                            )}
                                          </span>
                                        ) : displayLowestRate ? (
                                          <span className="text-gray-600">
                                            From <strong>${displayLowestRate.toLocaleString()}</strong>/night
                                            {item.override_lowest_room_rate && (
                                              <span className="ml-1 text-xs text-gray-500">(override)</span>
                                            )}
                                          </span>
                                        ) : null}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              {item.notes?.trim() && (
                                <div className="mt-3 p-2 bg-success-100 rounded text-sm text-success-800 whitespace-pre-wrap">
                                  <span className="mr-2 align-middle"><NoteVisibilityBadge clientFacing={item.notes_client_facing === true} /></span>
                                  <strong>Note:</strong> {item.notes}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
          
        </div>
      </div>
      
      {/* Dialogs */}
      {editingDestination && (
        <DestinationEditorModal
          isOpen={true}
          onClose={() => setEditingDestination(null)}
          city={editingDestination.city}
          country={editingDestination.country}
          existingContent={destinationContent.get(`${editingDestination.city}-${editingDestination.country}`)}
          researchDestinationId={editingDestination.researchDestinationId}
          weatherDestinationId={editingDestination.weatherDestinationId}
          activitySourceOptions={data?.grouped_by_city.find(g=>g.city===editingDestination.city && g.country===editingDestination.country)?.activity_sources || []}
          onActivitySourceChange={async (id) => {
            await researchService.selectProposalActivitySource(eventId,editingDestination.city,editingDestination.country,id);
            setEditingDestination(prev=>prev?{...prev,researchDestinationId:id}:prev);
            await loadData(true);
          }}
          eventData={eventData || null}
          onSave={handleDestinationContentSaved}
          onActivitiesChanged={() => { void loadData(true); }}
        />
      )}
      
      {configLinkType && (
        <ShareLinkConfigDialog
          isOpen={true}
          onClose={() => setConfigLinkType(null)}
          eventId={eventId}
          linkType={configLinkType}
          existingLinks={shareLinks}
          onLinkCreated={handleLinkCreated}
          onLinkUpdated={handleLinkUpdated}
          onLinkDeleted={handleLinkDeleted}
        />
      )}

      {editingVenueCost && (
        <VenueCostOverrideModal
          isOpen={true}
          onClose={() => setEditingVenueCost(null)}
          shortlistItem={editingVenueCost}
          onSave={handleVenueCostSaved}
        />
      )}

      <ProposalAnalyticsDrawer
        isOpen={showProposalAnalytics}
        onClose={() => setShowProposalAnalytics(false)}
        eventId={eventId}
        linkToken={proposalLink?.token ?? null}
      />

      <BudgetManager
        isOpen={showBudgetManager}
        onClose={() => setShowBudgetManager(false)}
        eventId={eventId}
        // Don't reload parent data on save - BudgetManager manages its own state
        // and reloading causes modal flicker
      />

      <AgendaManager
        isOpen={showAgendaManager}
        onClose={() => setShowAgendaManager(false)}
        eventId={eventId}
      />
    </div>
  );
}
